/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useReducer, useState, type ChangeEvent, type FocusEvent } from 'react';
import type { U2000FormSBusinessHandlers, U2000FormStateHandlersReturn } from '../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../../u2000Core/axios/U2000UseAxiosAuthenticated';
import U2000DefaultForm from '../../../u2000Core/components/formControls/U2000DefaultForm';
import { useToast } from '../../../u2000Core/context/toast/U2000ToastContext';
import { U2000HandleApiError } from '../../../u2000Core/axios/U2000HandleApiError';
import { useU2000GetHandlers } from '../../../u2000Core/hooks/handler/U2000UseGetHandlers';
import { u2000IntFormState, useU2000FormStateHandlers } from '../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { useU2000SearchHandlers } from '../../../u2000Core/hooks/handler/U2000UseSearchHandlers';
import type { U2000SearchPayload, U2000SearchResult } from '../../../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000GridStateEnumPaginationDirection } from '../../../u2000Core/reducer/formReducer/U2000GridState';
import { useU2000SaveHandlers } from '../../../u2000Core/hooks/handler/U2000UseSaveHandlers';
import u2000FormState, { U2000FormStateEnumEditMode, type U2000FormState } from '../../../u2000Core/reducer/formReducer/U2000FormState';
import { U2000LUStateEnumNextAction } from '../../../u2000Core/reducer/formReducer/U2000LUState';
import { useU2000DeleteHandlers } from '../../../u2000Core/hooks/handler/U2000UseDeleteHandlers';
import { U2000GridStateDeleteAction, type U2000GridStateNextRecordAfterDeleteReturn } from '../../../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import { NS, U3302ROLES } from '../../U3302';
import { U3302CategoryFormToolBar } from './U3302CategoryFormToolBar';
import U3302CategoryDeleteDlg from '../common/U3302CategoryDeleteDlg';
import { useU3302Context } from '../../U3302Context';
import U2000AuditPanelComponent from '../../../u2000Core/components/audit/U2000AuditPanelComponent';
import {
  buildCategoryCreateBody,
  buildCategoryDeleteBody,
  buildCategoryUpdateBody,
  createEmptyU3302CategoryFormRow,
  doCreateCategory,
  doDeleteCategory,
  doGetCategory,
  doGetCategoryAudits,
  doSearchCategories,
  doUpdateCategory,
  luHandleAppiError,
  normalizeU3302CategoryFromApi,
  type U3302CategoryFormRow,
  type U3302CategoryGet,
  type U3302CategorySearchRow,
} from '../../U3302Service';
import { useU2000AuthContext } from '../../../u2000Core/context/auth/U2000UseAuthContext';
import { useU2000LogicalUnitContext } from '../../../u2000Core/context/logicalUnit/U2000LogicalUnitContext';
import U2000Tab from '../../../u2000Core/components/tab/U2000Tab';
import U2000InputText from '../../../u2000Core/components/formControls/input/U2000InputText';
import U2000InvalidFeedback from '../../../u2000Core/components/formControls/feedback/U2000InvalidFeedback';
import { U2000FormStateEnumState } from '../../../u2000Core/reducer/formReducer/U2000FormState';
import { u3302IsHexColor } from '../../utils/u3302ColorUtils';

const CODE_PATTERN = /^[A-Z][A-Z0-9_]*$/;

export interface U3302CategoryFormExtendedHandlers extends U2000FormStateHandlersReturn<U3302CategoryFormRow> {
  addNewCategory: () => void;
  modifyCategory: () => void;
  deleteCategory: () => Promise<void>;
}

const U3302CategoryForm: React.FC = () => {
  const { id } = useParams();
  const { hasRole } = useU2000AuthContext();
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3302Context();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const listTitle = t('title', { ns: NS });
  const toast = useToast();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  const validateForm = (row: U3302CategoryFormRow) => {
    const result: Record<string, string> = {};
    const isAdd = !row.id;
    const isSystem = row.origin === 'SYSTEM';

    if (isSystem) {
      if (row.orgColorOverrideTouched && row.orgColorOverrideCode && !u3302IsHexColor(row.orgColorOverrideCode)) {
        result['orgColorOverrideCode'] = t('errColorPattern', { ns: NS });
      }
      return result;
    }

    if (isAdd) {
      const code = row.code?.trim().toUpperCase() ?? '';
      if (!code) result['code'] = t('errCodeRequired', { ns: NS });
      else if (code.length > 64) result['code'] = t('errCodeMax', { ns: NS });
      else if (!CODE_PATTERN.test(code)) result['code'] = t('errCodePattern', { ns: NS });
    }

    if (!row.label?.trim()) result['label'] = t('errLabelRequired', { ns: NS });
    if (!row.colorCode?.trim()) result['colorCode'] = t('errColorRequired', { ns: NS });
    else if (!u3302IsHexColor(row.colorCode)) result['colorCode'] = t('errColorPattern', { ns: NS });

    return result;
  };

  const u3302BL: U2000FormSBusinessHandlers<U3302CategoryFormRow> = {
    newRecord: () => createEmptyU3302CategoryFormRow(),
    validateForm,
    saveForme: async (row: U3302CategoryFormRow, editMode: U2000FormStateEnumEditMode) => {
      const errors = validateForm(row);
      if (Object.keys(errors).length > 0) {
        u3302FormExtended.setErrors(errors);
        return;
      }
      handleSave.saveData(row, editMode);
    },
    cancelForm: () => {
      const old = u3302FormExtended.getOldData() as U3302CategoryFormRow | null;
      if (old) u3302FormExtended.setData({ ...old });
      if (luStateHandlers.getActionOriginFrom() === '/U3302') {
        navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
      }
    },
    onChangeRecordForm: (row) => {
      if (row.id) handleGet.getData(row.id);
    },
    onChangePageForm: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },
    onLeaveForm: () => {
      navigate('/U3302');
    },
  };

  const u3302FormHandlers = useU2000FormStateHandlers<U3302CategoryFormRow>({
    formState,
    formStateDispatch,
    businessHandlers: u3302BL,
  });

  const u3302FormExtended: U3302CategoryFormExtendedHandlers = {
    ...u3302FormHandlers,
    addNewCategory: () => {
      u3302FormExtended.setEditMode(U2000FormStateEnumEditMode.ADD);
      u3302FormExtended.setData(createEmptyU3302CategoryFormRow());
      navigate('/U3302/new', { replace: true });
    },
    modifyCategory: () => {
      u3302FormExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },
    deleteCategory: async () => {
      const data = u3302FormExtended.getData();
      if (data?.origin === 'SYSTEM') return;
      handleDelete.handleDelete();
    },
  };

  const handleSearch = useU2000SearchHandlers<U3302CategorySearchRow>({
    doSearchFn: (searchPayload: U2000SearchPayload) => doSearchCategories(u2000AxiosAuthenticated, searchPayload),
    onSuccess: (searchResult: U2000SearchResult<U3302CategorySearchRow>, direction: U2000GridStateEnumPaginationDirection) => {
      const idx = gridStateHandlers.initGridAfterSearch(searchResult, direction);
      handleGet.getData(searchResult.data[idx].id);
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleGet = useU2000GetHandlers<U3302CategoryGet>({
    doGetFn: (rowId: string) => doGetCategory(u2000AxiosAuthenticated, rowId),
    onSuccess: (row: U3302CategoryGet) => {
      const normalized = normalizeU3302CategoryFromApi(row);
      gridStateHandlers.setDataById(normalized.id, {
        id: normalized.id,
        code: normalized.code,
        label: normalized.label,
        colorCode: normalized.colorCode,
        effectiveColorCode: normalized.effectiveColorCode,
        origin: normalized.origin,
        linkedLogicalUnit: normalized.linkedLogicalUnit,
        editableInCalendar: normalized.editableInCalendar,
        active: normalized.active,
        dateMode: normalized.dateMode,
        version: normalized.version,
      });
      u3302FormExtended.setData(normalized);
      const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(row.id));
      navigate(newPath, { replace: true });
      setLogicalUnitBreadcrumb([
        { href: '/U3302', label: listTitle },
        { label: `${normalized.code} — ${normalized.label}` },
      ]);
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        u3302FormExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleSave = useU2000SaveHandlers<U3302CategoryFormRow>({
    doSaveFn: (row: U3302CategoryFormRow, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        return doCreateCategory(u2000AxiosAuthenticated, buildCategoryCreateBody(row));
      }
      if (editMode === U2000FormStateEnumEditMode.EDIT) {
        return doUpdateCategory(u2000AxiosAuthenticated, row.id, buildCategoryUpdateBody(row));
      }
      return Promise.resolve(undefined);
    },
    onSuccess: (apiRow: U3302CategoryFormRow, editMode: U2000FormStateEnumEditMode) => {
      const normalized = normalizeU3302CategoryFromApi(apiRow as unknown as U3302CategoryGet);
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        gridStateHandlers.setData([
          ...gridStateHandlers.getData(),
          {
            id: normalized.id,
            code: normalized.code,
            label: normalized.label,
            colorCode: normalized.colorCode,
            effectiveColorCode: normalized.effectiveColorCode,
            origin: normalized.origin,
            linkedLogicalUnit: normalized.linkedLogicalUnit,
            editableInCalendar: normalized.editableInCalendar,
            active: normalized.active,
            dateMode: normalized.dateMode,
            version: normalized.version,
          },
        ]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
        navigate(`/U3302/${normalized.id}`, { replace: true });
      } else {
        gridStateHandlers.setDataById(normalized.id, {
          id: normalized.id,
          code: normalized.code,
          label: normalized.label,
          colorCode: normalized.colorCode,
          effectiveColorCode: normalized.effectiveColorCode,
          origin: normalized.origin,
          linkedLogicalUnit: normalized.linkedLogicalUnit,
          editableInCalendar: normalized.editableInCalendar,
          active: normalized.active,
          dateMode: normalized.dateMode,
          version: normalized.version,
        });
      }
      u3302FormExtended.afterSaveData(normalized);
      u3302FormExtended.setData(normalized);
      toast.success(t('msgLogicalUnitSaved', { ns: NS }));
      if (
        luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.ADD ||
        luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY
      ) {
        if (luStateHandlers.getActionOriginFrom() === '/U3302') {
          navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
        }
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        u3302FormExtended.setErrors(errorResult.fieldErrors);
      }
    },
  });

  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => {
      const data = u3302FormExtended.getData();
      return data
        ? [
            {
              id: data.id,
              code: data.code,
              label: data.label,
              colorCode: data.colorCode,
              effectiveColorCode: data.effectiveColorCode,
              origin: data.origin,
              linkedLogicalUnit: data.linkedLogicalUnit,
              editableInCalendar: data.editableInCalendar,
              active: data.active,
              dateMode: data.dateMode,
              version: data.version,
            } as U3302CategorySearchRow,
          ]
        : [];
    },
    doDeleteFn: async (_rowId) => {
      const data = u3302FormExtended.getData();
      if (!data) return;
      return await doDeleteCategory(u2000AxiosAuthenticated, buildCategoryDeleteBody(data));
    },
    onSuccess: (deletedRows) => {
      let newData = gridStateHandlers.getData();
      const currentIdx = gridStateHandlers.getCurrentIdx();
      let totalRows = gridStateHandlers.getTotalRows();
      deletedRows.forEach((del) => {
        toast.success(t('success0003', { key: (del as U3302CategorySearchRow).code, ns: NS }), t('successTitle', { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== del?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);
      const action: U2000GridStateNextRecordAfterDeleteReturn = gridStateHandlers.getNextRecordAfterDelete(currentIdx, newData, totalRows);
      if (action.action === U2000GridStateDeleteAction.RECORD && action.id) {
        handleGet.getData(action.id);
      } else if (action.action === U2000GridStateDeleteAction.LIST) {
        navigate('/U3302', { replace: true });
      }
    },
    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  useEffect(() => {
    if (id === undefined) return;
    if (id === 'new') {
      u3302FormExtended.setEditMode(U2000FormStateEnumEditMode.ADD);
      u3302FormExtended.setData(createEmptyU3302CategoryFormRow());
      setLogicalUnitBreadcrumb([
        { href: '/U3302', label: listTitle },
        { label: t('btnAdd', { ns: NS }) },
      ]);
    } else {
      handleGet.getData(id);
    }
  }, [id]);

  const data = u3302FormExtended.getData();
  if (data === null) return null;

  const readonly = u3302FormExtended.getFormState() === U2000FormStateEnumState.READ;
  const isSystem = data.origin === 'SYSTEM';
  const isCreate = u3302FormExtended.getFormEditMode() === U2000FormStateEnumEditMode.ADD;

  const setField = (fieldName: string, value: string | number | boolean | null | unknown[]) => {
    const ev = { target: { id: fieldName } } as ChangeEvent<HTMLInputElement>;
    u3302FormExtended.onChange?.(ev, value as string | number | boolean | any[]);
  };

  const blurField = (fieldName: string) => {
    u3302FormExtended.onBlur?.({ target: { id: fieldName } } as FocusEvent<HTMLInputElement>);
  };

  const formTabs = [
    {
      id: 'tabMain',
      label: t('lblLabel', { ns: NS }),
      content: (
        <div className="space-y-4 p-2">
          {isSystem && <p className="rounded-sm bg-slate-100 px-3 py-2 text-sm text-slate-700">{t('lblSystemCategoryBadge', { ns: NS })}</p>}

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <U2000InputText
              formStateHandlers={u3302FormExtended as any}
              fieldName="code"
              label={t('lblCode', { ns: NS })}
              readonly={readonly || !isCreate || isSystem}
              required={isCreate && !isSystem}
            />
            <U2000InputText
              formStateHandlers={u3302FormExtended as any}
              fieldName="label"
              label={t('lblLabel', { ns: NS })}
              readonly={readonly || isSystem}
              required={!isSystem}
            />
          </div>

          {!isSystem && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <label className="block space-y-1">
                <span className="text-sm font-medium text-gray-700">{t('lblColorCode', { ns: NS })}</span>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    className="h-10 w-14 cursor-pointer rounded-sm border border-gray-300"
                    value={u3302IsHexColor(data.colorCode) ? data.colorCode : '#2563EB'}
                    disabled={readonly}
                    onChange={(e) => setField('colorCode', e.target.value.toUpperCase())}
                    onBlur={() => blurField('colorCode')}
                  />
                  <input
                    className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100"
                    value={data.colorCode}
                    disabled={readonly}
                    onChange={(e) => setField('colorCode', e.target.value)}
                    onBlur={() => blurField('colorCode')}
                  />
                </div>
                <U2000InvalidFeedback formStateHandlers={u3302FormExtended as any} fieldName="colorCode" />
              </label>

              <fieldset className="space-y-2 pt-1">
                <legend className="text-sm font-medium text-gray-700">{t('lblDateMode', { ns: NS })}</legend>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="dateMode"
                    checked={data.dateMode === 'SINGLE'}
                    disabled={readonly}
                    onChange={() => {
                      setField('dateMode', 'SINGLE');
                      setField('generateWeekendEvents', false);
                    }}
                  />
                  <span className="text-sm text-gray-700">{t('valDateModeSINGLE', { ns: NS })}</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="dateMode"
                    checked={data.dateMode === 'INTERVAL'}
                    disabled={readonly}
                    onChange={() => setField('dateMode', 'INTERVAL')}
                  />
                  <span className="text-sm text-gray-700">{t('valDateModeINTERVAL', { ns: NS })}</span>
                </label>
              </fieldset>

              {data.dateMode === 'INTERVAL' && (
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={data.generateWeekendEvents}
                    disabled={readonly}
                    onChange={(e) => setField('generateWeekendEvents', e.target.checked)}
                  />
                  <span className="text-sm text-gray-700">{t('lblGenerateWeekendEvents', { ns: NS })}</span>
                </label>
              )}

              <label className="flex items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  checked={data.editableInCalendar}
                  disabled={readonly}
                  onChange={(e) => setField('editableInCalendar', e.target.checked)}
                />
                <span className="text-sm text-gray-700">{t('lblEditableInCalendar', { ns: NS })}</span>
              </label>

              <label className="flex items-center gap-2">
                <input type="checkbox" checked={data.active} disabled={readonly} onChange={(e) => setField('active', e.target.checked)} />
                <span className="text-sm text-gray-700">{t('lblActive', { ns: NS })}</span>
              </label>
            </div>
          )}

          {isSystem && (
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                {t('lblDateMode', { ns: NS })}:{' '}
                <span className="font-medium text-gray-800">{t(`valDateMode${data.dateMode}`, { ns: NS })}</span>
              </p>
              <div className="text-sm text-gray-600">
                {t('lblEffectiveColor', { ns: NS })}:{' '}
                <span className="inline-flex items-center gap-1.5 font-medium">
                  <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: data.effectiveColorCode }} />
                  {data.effectiveColorCode}
                </span>
              </div>
              <label className="block space-y-1">
                <span className="text-sm font-medium text-gray-700">{t('lblOrgColorOverride', { ns: NS })}</span>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="color"
                    className="h-10 w-14 cursor-pointer rounded-sm border border-gray-300 disabled:opacity-50"
                    value={u3302IsHexColor(data.orgColorOverrideCode ?? '') ? data.orgColorOverrideCode! : data.colorCode}
                    disabled={readonly}
                    onChange={(e) => {
                      setField('orgColorOverrideCode', e.target.value.toUpperCase());
                      setField('orgColorOverrideTouched', true);
                    }}
                  />
                  <input
                    className="min-w-[120px] flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100"
                    value={data.orgColorOverrideCode ?? ''}
                    disabled={readonly}
                    placeholder={data.colorCode}
                    onChange={(e) => {
                      setField('orgColorOverrideCode', e.target.value);
                      setField('orgColorOverrideTouched', true);
                    }}
                  />
                  {!readonly && (
                    <button
                      type="button"
                      className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-50"
                      onClick={() => {
                        setField('orgColorOverrideCode', null);
                        setField('orgColorOverrideTouched', true);
                      }}
                    >
                      {t('btnResetColorOverride', { ns: NS })}
                    </button>
                  )}
                </div>
                <U2000InvalidFeedback formStateHandlers={u3302FormExtended as any} fieldName="orgColorOverrideCode" />
              </label>
            </div>
          )}

          {!isCreate && (
            <p className="text-sm text-gray-600">
              {t('lblActiveEventCount', { ns: NS })}:{' '}
              <span className="font-medium text-gray-800">{data.activeEventCount}</span>
            </p>
          )}
        </div>
      ),
    },
    data.id &&
      hasRole(U3302ROLES.AUDITS) && {
        id: 'tabAudits',
        label: t('tabAudits', { ns: NS }),
        content: (
          <U2000AuditPanelComponent
            id={data.id}
            doGetAuditsData={doGetCategoryAudits}
            toastName="U3302CategoryAudit"
          />
        ),
      },
  ].filter(Boolean) as { id: string; label: string; content: React.ReactNode }[];

  return (
    <>
      <U3302CategoryDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />
      <U2000DefaultForm
        actionsBtns={<U3302CategoryFormToolBar formStateHandlers={u3302FormExtended as any} />}
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        formStateHandlers={u3302FormExtended as any}
      >
        <U2000Tab
          className="mt-4"
          tabs={formTabs}
          defaultTab="tabMain"
          activeTab={activeTab ?? 'tabMain'}
          setActiveTab={(tab) => setActiveTab(typeof tab === 'string' ? tab : String(tab))}
        />
      </U2000DefaultForm>
    </>
  );
};

export default U3302CategoryForm;
